import { parseDateValue } from '../../utils/formatUtils';
import { makeAutoObservable, runInAction } from "mobx";
import { getAPIUrl } from "../../api/apiUtils";
import { apiFetch } from "../../api/apiClient";

export const groupIdToTitle = {
  "-80315714": "Администрация г. Снежинска",
  "-32444926": "Говорит Саров",
  "-80950633": "Говорит СНЕЖИНСК (Метрополия Снежинск)",
  "-59472296": "Говорят в Снежинске",
  "-35911547": "Затоновости Саров",
  "-48632629": "ИКТИБ ЮФУ",
  "-252491": "МЫ ИЗ САРОВА | НОВОСТИ САРОВ",
  "-61784388": "Новая таганрогская по ссылке",
  "-210246593": "Нововоронеж 24",
  "-140223588": "ПОДСЛУШАНО В СНЕЖИНСКЕ",
  "-59848442": "Подслушано Нововоронеж",
  "-62818821": "Подслушано САРОВ",
  "-152253978": "Подслушано Снежинск",
  "-818023": "Саров",
  "-101957040": "СИЦ ААИ",
  "-130544609": "Снежинск",
  "-203781938": "Снежинский Сплетник",
  "-148944189": "Снежинск.СОБЫТИЯ",
  "-891791717": "Таганрог",
  "-100105861": "Услышано в Снежинске"
};

class TragerCoefficientChartStore {
  data = [];
  sourceCount = null;
  dataByDateRange = [];
  meanValue = 0;
  stdDeviation = 0;
  minValue = 0;
  maxValue = 0;
  loading = false;
  error = null;
  startDate = '';
  endDate = '';
  selectedGroupIds = [];
  textLength = { min: '', max: '' };
  cache = {}; // Кэш для графика
  _ranges = null; // Кэш для ranges

  constructor() {
    makeAutoObservable(this);
  }

  // Универсальный парсер даты:
  // - поддерживает timestamp в секундах/миллисекундах и строки даты
  // - результат всегда timestamp в секундах
  parseDateToTimestamp(date) {
    const parsed = parseDateValue(date);
    return parsed ? Math.floor(parsed.getTime() / 1000) : null;
  }

  setFilters({ startDate, endDate, selectedGroupIds, textLength }) {
    this.textLength = textLength;
    this.startDate = startDate;
    this.endDate = endDate;
    this.selectedGroupIds = selectedGroupIds;
  }

  // Ключ кэша по фильтрам
  getCacheKey(filters) {
    return JSON.stringify(filters);
  }


  calculateMeanAndStdDev() {
    const values = this.data.map(item => item.trager_coef).filter(Number.isFinite);
    if (values.length === 0) {
      runInAction(() => {
        this.meanValue = 0;
        this.stdDeviation = 0;
        this.minValue = 0;
        this.maxValue = 0;
        this._ranges = null;
      });
      return;
    }
    if (values.length > 0) {
      const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
      
      // Проверяем, что у нас достаточно данных для расчета стандартного отклонения
      let stdDev = 0;
      if (values.length > 1) {
        // Исправляем расчет стандартного отклонения - используем n-1 для выборочного стандартного отклонения
        const variance = values.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / (values.length - 1);
        stdDev = Math.sqrt(variance);
      } else {
        // Если только одно значение, устанавливаем стандартное отклонение как 10% от значения
        stdDev = Math.abs(mean) * 0.1;
      }
      
      const min = Math.min(...values);
      const max = Math.max(...values);
      runInAction(() => {
        this.meanValue = mean;
        this.stdDeviation = stdDev;
        this.minValue = min;
        this.maxValue = max;
      });
      // Пересчитываем ranges после обновления статистики
      this.calculateRanges();
    }
  }

  // Метод для расчета ranges
  calculateRanges() {
    if (!this.data.some(item => Number.isFinite(item.trager_coef))) {
      this._ranges = null;
      return;
    }

    this._ranges = {
      // Зеленая зона: ±1σ (68.27% данных)
      green: [this.meanValue - this.stdDeviation, this.meanValue + this.stdDeviation],
      
      // Серая зона: ±2σ (95.45% данных)
      gray: [
        [this.meanValue - 2 * this.stdDeviation, this.meanValue - this.stdDeviation], 
        [this.meanValue + this.stdDeviation, this.meanValue + 2 * this.stdDeviation]
      ],
      
      // Оранжевая зона: ±3σ (99.73% данных)
      orange: [
        [this.meanValue - 3 * this.stdDeviation, this.meanValue - 2 * this.stdDeviation], 
        [this.meanValue + 2 * this.stdDeviation, this.meanValue + 3 * this.stdDeviation]
      ],
      
      // Красная зона: за пределами ±3σ (0.27% данных)
      red: [
        [this.minValue, this.meanValue - 3 * this.stdDeviation], 
        [this.meanValue + 3 * this.stdDeviation, this.maxValue]
      ],
    };

    // Отладочная информация
    console.log('Статистика коэффициента Трейгера:', {
      количество_точек: this.data.length,
      среднее_значение: this.meanValue.toFixed(4),
      стандартное_отклонение: this.stdDeviation.toFixed(4),
      минимум: this.minValue.toFixed(4),
      максимум: this.maxValue.toFixed(4),
      зоны: {
        зеленая: this._ranges.green.map(v => v.toFixed(4)),
        серая: this._ranges.gray.map(range => range.map(v => v.toFixed(4))),
        оранжевая: this._ranges.orange.map(range => range.map(v => v.toFixed(4))),
        красная: this._ranges.red.map(range => range.map(v => v.toFixed(4)))
      }
    });

    // Валидируем расчеты
    this.validateCalculations();
  }

  // Метод для валидации расчетов
  validateCalculations() {
    if (this.data.length === 0) {
      console.warn('Нет данных для валидации');
      return false;
    }

    const values = this.data.map(item => item.trager_coef).filter(Number.isFinite);
    const n = values.length;
    const mean = values.reduce((sum, val) => sum + val, 0) / n;
    
    // Проверяем расчет среднего
    if (Math.abs(mean - this.meanValue) > 1e-10) {
      console.error('Ошибка в расчете среднего значения:', { expected: mean, actual: this.meanValue });
      return false;
    }

    // Проверяем расчет стандартного отклонения
    if (n > 1) {
      const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (n - 1);
      const stdDev = Math.sqrt(variance);
      
      if (Math.abs(stdDev - this.stdDeviation) > 1e-10) {
        console.error('Ошибка в расчете стандартного отклонения:', { expected: stdDev, actual: this.stdDeviation });
        return false;
      }
    }

    // Проверяем границы зон
    const ranges = this.ranges;
    if (!ranges) {
      console.error('Ranges не рассчитаны');
      return false;
    }

    // Проверяем, что зоны не пересекаются
    if (ranges.green[1] > ranges.gray[0][0] || ranges.gray[1][1] > ranges.orange[0][0] || ranges.orange[1][1] > ranges.red[0][1]) {
      console.error('Зоны пересекаются');
      return false;
    }

    // console.log('✅ Валидация расчетов прошла успешно');
    return true;
  }

  // Геттер для получения ranges
  get ranges() {
    return this._ranges;
  }

  async fetchDataByDateRange(startDate, endDate) {
    runInAction(() => {
      // this.loading = true;
      this.error = null;
    });
  
    try {
      const filters = {
        startDate,
        endDate,
        selectedGroupIds: this.selectedGroupIds,
        textLength: this.textLength,
      };
      const cacheKey = `${this.getCacheKey(filters)}_trager_detail_v4`;
  
      if (this.cache[cacheKey]) {
        console.log('Using cached data, length:', this.cache[cacheKey].length);
        this.dataByDateRange = this.cache[cacheKey];
        runInAction(() => this.loading = false);
        this.calculateMeanAndStdDev();
        return;
      }
  
      if (!this.selectedGroupIds.length) {
        runInAction(() => {
          this.dataByDateRange = [];
          this.loading = false;
        });
        this.calculateMeanAndStdDev();
        return;
      }
  
      // Пагинация: Загружаем все страницы
      let allArr = [];
      let page = 1;
      const limit = 10000;
      let hasMore = true;
  
      while (hasMore) {
        const params = {
          page,
          limit,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          sort_by: 'date',
          sort_order: 'asc',
          columns: 'trager_coef,date,verbs_per_nouns_coef,group_id,post_id,comment_id,author_id,text',
          group_ids: this.selectedGroupIds.join(','),
          min_text_length: this.textLength.min,
          max_text_length: this.textLength.max,
        };
  
        const url = `${getAPIUrl()}/detection/text_markers_analysis`;
        const query = new URLSearchParams(params).toString();
        const response = await apiFetch(`${url}?${query}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
  
        const result = await response.json();
  
        let arr = Array.isArray(result) ? result : (result.data || []);
        allArr = allArr.concat(arr);

        console.log(allArr)
  
        hasMore = (arr.length === limit) && (page < (result.total / limit || 10));
        page++;
      }
  
      // Преобразование данных
      let filtered = allArr
        .map(item => ({
          date: this.parseDateToTimestamp(item.date),  // timestamp (секунды)
          trager_coef: item.trager_coef == null ? null : Number(item.trager_coef),
          verbs_per_nouns_coef: item.verbs_per_nouns_coef == null ? null : Number(item.verbs_per_nouns_coef),
          group_id: item.group_id,
          post_id: item.post_id,
          comment_id: item.comment_id,
          author_id: item.author_id,
          text: item.text,
        }))
        .filter(item =>
          item.date &&
          (item.trager_coef === null || Number.isFinite(item.trager_coef)) &&
          (item.verbs_per_nouns_coef === null || Number.isFinite(item.verbs_per_nouns_coef))
        );
  
      // Без группировки: оставляем данные постов как есть и сортируем по дате
      if (filtered.length > 0) {
        filtered = filtered.sort((a, b) => a.date - b.date);
      }

      // Преобразуем ключи на русские и добавим ссылки на пост/комментарий ВК
      const displayRows = filtered.map(item => {
        const groupIdNum = Number(item.group_id);
        const vkGroupId = isNaN(groupIdNum) ? '' : (groupIdNum > 0 ? -groupIdNum : groupIdNum);
        const postLink = (vkGroupId !== '' && item.post_id) ? `https://vk.com/wall${vkGroupId}_${item.post_id}` : '';
        const commentLink = (vkGroupId !== '' && item.post_id && item.comment_id) ? `https://vk.com/wall${vkGroupId}_${item.post_id}?reply=${item.comment_id}` : '';
        const dateStr = item.date ? new Date(item.date * 1000).toISOString().slice(0, 10) : '';
        const groupTitle = groupIdToTitle[String(item.group_id)] || item.group_id;
        const authorLink = item.author_id ? `https://vk.com/id${item.author_id}` : '';
        return {
          'Дата': dateStr,
          'Коэффициент Трейгера': item.trager_coef,
          'Коэффициент глаголов к существительным': item.verbs_per_nouns_coef,
          'Группа': groupTitle,
          'ID поста': item.post_id,
          'ID комментария': item.comment_id,
          'Автор': authorLink || '-',
          'Текст': item.text || '',
          'Ссылка на пост': postLink || '-',
          'Ссылка на комментарий': commentLink || '-',
        };
      });
  
      runInAction(() => {
        this.dataByDateRange = displayRows;
        this.cache[cacheKey] = displayRows;
      });
    } catch (e) {
      runInAction(() => {
        this.dataByDateRange = [];
        this.error = e;
        console.error('Fetch error:', e);
      });
    } finally {
      runInAction(() => this.loading = false);
    }
  
    this.calculateMeanAndStdDev();
  }
  

  async fetchData() {
    runInAction(() => {
      this.loading = true;
      this.error = null;
      this.sourceCount = null;
    });
  
    try {
      const filters = {
        startDate: this.startDate,
        endDate: this.endDate,
        selectedGroupIds: this.selectedGroupIds,
        textLength: this.textLength,
      };
      const cacheKey = `${this.getCacheKey(filters)}_trager_chart_v5`;
  
      if (this.cache[cacheKey]) {
        console.log('Using cached data, length:', this.cache[cacheKey].data.length);
        this.data = this.cache[cacheKey].data;
        this.sourceCount = this.cache[cacheKey].sourceCount;
        runInAction(() => this.loading = false);
        this.calculateMeanAndStdDev();
        return;
      }
  
      if (!this.selectedGroupIds.length) {
        runInAction(() => {
          this.data = [];
          this.sourceCount = 0;
          this.loading = false;
        });
        this.calculateMeanAndStdDev();
        return;
      }
  
      // Пагинация: Загружаем все страницы
      let allArr = [];
      let page = 1;
      const limit = 10000;
      let hasMore = true;
      let sourceCount = null;
  
      while (hasMore) {
        const params = {
          page,
          limit,
          start_date: this.startDate || undefined,
          end_date: this.endDate || undefined,
          group_ids: this.selectedGroupIds.join(','),
          min_text_length: this.textLength.min,
          max_text_length: this.textLength.max,
          interval: 1,
        };
  
        const url = `${getAPIUrl()}/detection/trager_chart_daily`;
        const query = new URLSearchParams(params).toString();
        const response = await apiFetch(`${url}?${query}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const result = await response.json();
        if (page === 1 && Number.isFinite(Number(result.source_count))) {
          sourceCount = Number(result.source_count);
        }
        // console.log(`Page ${page}: total=${result.total || 'unknown'}, page data length=${(result.data || []).length}`);
  
        let arr = Array.isArray(result) ? result : (result.data || []);
        allArr = allArr.concat(arr);        
  
        hasMore = page * limit < result.total;
        page++;
      }
  
      // console.log('Total raw data length:', allArr.length);  // Должно быть ~23260
      // console.log('All data:', allArr);
  
      const filtered = allArr
        .map(item => ({
          date: this.parseDateToTimestamp(item.date),  // timestamp (секунды)
          trager_coef: item.trager_coef == null ? null : Number(item.trager_coef),
          verbs_per_nouns_coef: item.verbs_per_nouns_coef == null ? null : Number(item.verbs_per_nouns_coef),
        }))
        .filter(item => item.date && (Number.isFinite(item.trager_coef) || Number.isFinite(item.verbs_per_nouns_coef)));
      
  
      runInAction(() => {
        this.data = filtered;
        this.sourceCount = sourceCount;
        this.cache[cacheKey] = { data: filtered, sourceCount };
      });
    } catch (e) {
      runInAction(() => {
        this.data = [];
        this.sourceCount = null;
        this.error = e;
        console.error('Fetch error:', e);
      });
    } finally {
      runInAction(() => this.loading = false);
    }
  
    this.calculateMeanAndStdDev();
  }

}

export const tragerCoefficientChartStore = new TragerCoefficientChartStore();
export const actionObjectificationChartStore = new TragerCoefficientChartStore();
